import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ShoppingCart, Plus, Minus, Store as StoreIcon, MessageCircle, 
  Instagram, Facebook, Linkedin, Send, Music, X, AlertTriangle,
  Info, Package, Menu, Grid3x3, MapPin, Video, ChevronLeft, ChevronRight, Play, UtensilsCrossed
} from 'lucide-react';
import { 
  getStoreById, getProductsByStore, createOrder, 
  getStoreCustomization, getStoreBanners, getStoreCategories
} from '../lib/firebaseService';

export default function PublicStore() {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customization, setCustomization] = useState(null);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    notes: ''
  });

  // Mağaza telefon nömrəsi (WhatsApp üçün) - prioritet: whatsapp_number > phone_number
  const storeWhatsApp = store?.whatsapp_number || store?.phone_number || '';
  
  // Debug log
  useEffect(() => {
    if (store) {
      console.log('Store data:', {
        id: store.id,
        name: store.store_name,
        phone_number: store.phone_number,
        whatsapp_number: store.whatsapp_number,
        owner_id: store.owner_id
      });
      console.log('Using WhatsApp number:', storeWhatsApp);
    }
  }, [store, storeWhatsApp]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart.length >= 0) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);

  const loadCartFromStorage = () => {
    try {
      const cartData = localStorage.getItem('cart');
      if (cartData) {
        const parsedCart = JSON.parse(cartData);
        // Filter cart items for current store only if storeId is available
        if (storeId) {
          const storeCart = parsedCart.filter(item => item.store_id === storeId);
          setCart(storeCart);
        } else {
          setCart([]);
        }
      }
    } catch (error) {
      console.error('Səbət yüklənə bilmədi:', error);
      setCart([]);
    }
  };

  useEffect(() => {
    loadStoreData();
  }, [storeId]);

  // Load cart after storeId is available
  useEffect(() => {
    if (storeId) {
      loadCartFromStorage();
    }
  }, [storeId]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      const [storeData, productsData, customData, bannersData, categoriesData] = await Promise.all([
        getStoreById(storeId),
        getProductsByStore(storeId),
        getStoreCustomization(storeId),
        getStoreBanners(storeId).catch(() => []),
        getStoreCategories(storeId).catch(() => [])
      ]);
      setStore(storeData);
      setProducts(productsData);
      setCustomization(customData);
      setBanners(bannersData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Məlumat yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    const currentStock = product.stock || 999;
    
    // Check if product is out of stock
    if (currentStock <= 0) {
      toast.error('Bu məhsul stokda yoxdur');
      return;
    }
    
    let newCart;
    if (existing) {
      if (existing.quantity >= currentStock) {
        toast.error('Stokda kifayət qədər məhsul yoxdur');
        return;
      }
      newCart = cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      newCart = [...cart, { 
        ...product, 
        quantity: 1,
        store_id: storeId 
      }];
    }
    
    setCart(newCart);
    toast.success('Səbətə əlavə edildi');
  };

  // Check if product can be added to cart
  const canAddToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    const currentStock = product.stock || 999;
    
    if (currentStock <= 0) return false;
    if (existing && existing.quantity >= currentStock) return false;
    return true;
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQuantity = item.quantity + delta;
        if (newQuantity <= 0) return null;
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(Boolean));
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Səbət boşdur');
      return;
    }
    setShowCheckout(true);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!storeWhatsApp) {
      toast.error('Mağazanın WhatsApp nömrəsi yoxdur');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        store_id: storeId,
        customer_name: customerData.name,
        customer_phone: customerData.phone,
        notes: customerData.notes,
        status: 'pending',
        total: parseFloat(getTotal()),
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          price: item.price,
          quantity: item.quantity
        }))
      };

      await createOrder(orderData);

      const items = cart.map(item => `• ${item.name} x${item.quantity} - ${item.price * item.quantity} AZN`).join('\n');
      const message = `🛒 *Yeni Sifariş!*\n\n👤 *Müştəri:* ${customerData.name}\n📱 *Telefon:* ${customerData.phone}\n\n📦 *Məhsullar:*\n${items}\n\n💰 *Cəmi:* ${getTotal()} AZN${customerData.notes ? `\n\n📝 *Qeyd:* ${customerData.notes}` : ''}`;

      const whatsappNumber = storeWhatsApp.replace(/[^0-9]/g, '');
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      
      toast.success('Sifariş göndərildi!');
      setTimeout(() => window.open(whatsappUrl, '_blank'), 500);

      setCart([]);
      setShowCheckout(false);
      setCustomerData({ name: '', phone: '', notes: '' });
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category_id === selectedCategory);

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '';
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const layout = customization?.layout || 'grid';
  const customColor = customization?.customColor || '#FFFFFF';

  // Text rəngini customColor-a görə təyin et
  const getTextColor = () => {
    if (customColor === '#000000') {
      return 'text-white'; // Qara background - ağ text
    }
    return 'text-black'; // Default - qara text
  };

  const textColorClass = getTextColor();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-3"></div>
          <p className="text-gray-400">Yüklənir...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <StoreIcon className="w-16 h-16 text-gray-300 mb-4" />
        <h1 className="text-xl font-bold mb-2">Mağaza tapılmadı</h1>
        <p className="text-gray-500 mb-4 text-sm text-center">Bu mağaza mövcud deyil və ya silinib.</p>
        <Link to="/">
          <Button size="sm">Ana səhifəyə qayıt</Button>
        </Link>
      </div>
    );
  }

  // Deaktiv mağaza
  if (store.is_active === false) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-xl font-bold mb-2 text-gray-800">Mağaza Deaktivdir</h1>
          <p className="text-gray-600 mb-5 text-sm">
            Bu mağaza admin tərəfindən deaktiv edilib. Ətraflı məlumat üçün bizimlə əlaqə saxlayın.
          </p>
          <a
            href={`https://wa.me/${storeWhatsApp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Salam, mağazam deaktiv edilib. Kömək edə bilərsinizmi?')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-xl font-medium transition-colors text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp ilə Əlaqə
          </a>
        </div>
      </div>
    );
  }

  const theme = customization?.theme || 'classic';
  
  // Rəngə görə tema təyin et
  const getThemeByColor = () => {
    if (customColor === '#000000') {
      // Qara background - ağ text/elementlər
      return {
        bg: 'bg-black',
        header: 'bg-black text-white border-b border-gray-800',
        card: 'bg-gray-900',
        button: 'bg-white hover:bg-gray-100 text-black',
        accent: 'text-white',
        border: 'border-gray-800',
        tabActive: 'bg-white text-black'
      };
    } else if (customColor === '#FFFFFF') {
      // Ağ background - qara text/elementlər
      return {
        bg: 'bg-white',
        header: 'bg-black text-white',
        card: 'bg-gray-50',
        button: 'bg-black hover:bg-gray-800 text-white',
        accent: 'text-black',
        border: 'border-gray-200',
        tabActive: 'bg-black text-white'
      };
    }
    return null;
  };

  const themeClasses = {
    classic: {
      bg: 'bg-white',
      header: 'bg-black text-white',
      card: 'bg-gray-50',
      button: 'bg-black hover:bg-gray-800 text-white',
      accent: 'text-black',
      border: 'border-gray-200',
      tabActive: 'bg-black text-white'
    },
    modern: {
      bg: 'bg-gray-100',
      header: 'bg-gray-900 text-white',
      card: 'bg-white',
      button: 'bg-gray-800 hover:bg-gray-900',
      accent: 'text-gray-800',
      border: 'border-gray-200',
      tabActive: 'bg-gray-800 text-white'
    },
    elegant: {
      bg: 'bg-amber-50',
      header: 'bg-amber-800 text-white',
      card: 'bg-white',
      button: 'bg-amber-700 hover:bg-amber-800',
      accent: 'text-amber-700',
      border: 'border-amber-100',
      tabActive: 'bg-amber-700 text-white'
    },
    vibrant: {
      bg: 'bg-gradient-to-br from-purple-50 to-pink-50',
      header: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
      card: 'bg-white',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600',
      accent: 'text-purple-600',
      border: 'border-purple-100',
      tabActive: 'bg-purple-600 text-white'
    }
  };

  // Custom rəng varsa onun temasını, yoxsa seçilmiş temanı istifadə et
  const currentTheme = getThemeByColor() || themeClasses[theme];

  // Background və text rənglərini təyin et
  const isBlackTheme = customColor === '#000000';
  const bgClass = isBlackTheme ? 'bg-black' : 'bg-white';
  const textClass = isBlackTheme ? 'text-white' : 'text-gray-900';
  const textMutedClass = isBlackTheme ? 'text-gray-400' : 'text-gray-500';
  const borderClass = isBlackTheme ? 'border-gray-800' : 'border-gray-100';
  const hoverBgClass = isBlackTheme ? 'hover:bg-gray-900' : 'hover:bg-gray-100';
  const cardBgClass = isBlackTheme ? 'bg-gray-900' : 'bg-white';
  const buttonClass = isBlackTheme ? 'bg-white text-black' : 'bg-black text-white';

  return (
    <div className={`min-h-screen flex flex-col ${bgClass}`}>
      {/* Made with I Veb - Sol aşağı köşə */}
      <a
        href="/"
        className="fixed right-4 bottom-4 z-40 flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 text-sm"
      >
        <span className="text-gray-500">Made with</span>
        <span className="font-bold text-black">I Veb</span>
      </a>

      {/* Header - TakeApp Style */}
      <header className={`sticky top-0 z-20 ${bgClass} border-b ${borderClass}`}>
        <div className="flex items-center justify-between px-4 py-4">
          {/* Sol - Hamburger Menu */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className={`p-2 ${hoverBgClass} rounded-lg transition-colors`}
          >
            <Menu className={`w-7 h-7 ${textClass}`} />
          </button>

          {/* Orta - Logo və Ad */}
          <div className="flex items-center gap-3">
            {store.logo_url && (
              <img src={store.logo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
            )}
            <span className={`font-semibold text-lg ${textClass} truncate max-w-[180px]`}>{store.store_name}</span>
          </div>

          {/* Sağ - Səbət */}
          <button
            onClick={handleCheckout}
            className={`relative p-2 ${hoverBgClass} rounded-lg transition-colors`}
          >
            <ShoppingCart className={`w-7 h-7 ${textClass}`} />
            {cartItemCount > 0 && (
              <span className={`absolute -top-1 -right-1 w-6 h-6 ${buttonClass} text-sm rounded-full flex items-center justify-center font-bold`}>
                {cartItemCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {/* Hero Section with Banner - WhatsApp Business Style */}
        <div className="relative">
          {/* Cover Background (WhatsApp Business Style) */}
          {banners.length > 0 && banners[0].image_url && (
            <div className="w-full h-48 md:h-56 overflow-hidden">
              <img 
                src={banners[0].image_url} 
                alt="Mağaza arxa fonu" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Logo & Brand Section */}
          <div className={`flex flex-col items-center ${banners.length > 0 ? '-mt-16' : 'pt-12'} px-6 pb-8 text-center`}>
            {/* Logo - Profil şəkli */}
            {store.logo_url ? (
              <div className={`w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden mb-4 shadow-xl border-4 ${bgClass} ${borderClass}`}>
                <img 
                  src={store.logo_url} 
                  alt={store.store_name} 
                  className="w-full h-full object-cover" 
                />
              </div>
            ) : (
              <div className={`w-32 h-32 md:w-40 md:h-40 rounded-full ${cardBgClass} flex items-center justify-center mb-4 shadow-xl border-4 ${bgClass}`}>
                <StoreIcon className={`w-16 h-16 ${textMutedClass}`} />
              </div>
            )}

            {/* Brand Name */}
            <h1 className={`text-2xl md:text-3xl font-bold ${textClass} mb-2`}>{store.store_name}</h1>
            
            {/* City */}
            {store.city && (
              <p className={`${textMutedClass} mb-6`}>{store.city}</p>
            )}

            {/* Category Button */}
            {categories.length > 0 && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className={`flex items-center gap-3 px-6 py-3 ${cardBgClass} ${hoverBgClass} rounded-xl transition-colors mb-4 border ${borderClass}`}
              >
                <Grid3x3 className={`w-5 h-5 ${textMutedClass}`} />
                <span className={`${textClass} font-medium`}>Kateqoriya</span>
              </button>
            )}

            {/* WhatsApp Contact */}
            {storeWhatsApp && (
              <a
                href={`https://wa.me/${storeWhatsApp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-6 py-3 ${cardBgClass} border ${borderClass} rounded-xl ${hoverBgClass} transition-colors mb-4`}
              >
                <MessageCircle className="w-5 h-5 text-green-600" />
                <span className={`${textClass} font-medium`}>WhatsApp ilə əlaqə</span>
              </a>
            )}

            {/* I Veb CTA - Profil altında */}
            <a
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-black to-gray-800 text-white rounded-full text-sm font-medium hover:from-gray-800 hover:to-black transition-all shadow-lg hover:shadow-xl hover:scale-105"
            >
              <span>✨</span>
              <span>Sən də öz vebsaytını yarat</span>
            </a>
          </div>
        </div>

        {/* Products Section */}
        {products.length > 0 && (
          <div className="px-4 pb-24">
            {/* Category Filter */}
            {categories.length > 0 && (
              <div className="overflow-x-auto scrollbar-hide mb-6 -mx-4 px-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === 'all'
                        ? buttonClass
                        : `${cardBgClass} ${textClass} border ${borderClass} ${hoverBgClass}`
                    }`}
                  >
                    Hamısı
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        selectedCategory === cat.id
                          ? buttonClass
                          : `${cardBgClass} ${textClass} border ${borderClass} ${hoverBgClass}`
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Products - Layout-a görə */}
            {layout === 'list' ? (
              /* List Layout */
              <div className="space-y-3">
                {filteredProducts.map((product) => {
                  const canAdd = canAddToCart(product);
                  return (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    className={`${cardBgClass} rounded-2xl overflow-hidden shadow-sm border ${borderClass} hover:shadow-md transition-all cursor-pointer block`}
                  >
                    <div className="flex gap-4 p-4">
                      <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                        {product.image_url || product.media?.[0]?.url ? (
                          <img
                            src={product.media?.[0]?.url || product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className={`w-full h-full ${isBlackTheme ? 'bg-gray-800' : 'bg-gray-100'} flex items-center justify-center`}>
                            <Package className={`w-8 h-8 ${textMutedClass}`} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-medium ${textClass} line-clamp-2 mb-1`}>{product.name}</h3>
                        {product.description && (
                          <p className={`text-sm ${textMutedClass} line-clamp-2 mb-2`}>{product.description}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className={`font-bold ${textClass}`}>{product.price} ₼</span>
                          {canAdd ? (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addToCart(product);
                              }}
                              className={`p-2 ${buttonClass} rounded-full transition-colors`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-xs text-red-500 font-medium px-2 py-1 bg-red-50 rounded-full">
                              Stokda bitdi
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                )})}
              </div>
            ) : (
              /* Grid Layout (Default) */
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredProducts.map((product) => {
                const canAdd = canAddToCart(product);
                return (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className={`${cardBgClass} rounded-2xl overflow-hidden shadow-sm border ${borderClass} hover:shadow-md transition-all cursor-pointer block`}
                >
                  <div className="aspect-square relative">
                    {product.image_url || product.media?.[0]?.url ? (
                      <img
                        src={product.media?.[0]?.url || product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full ${isBlackTheme ? 'bg-gray-800' : 'bg-gray-100'} flex items-center justify-center`}>
                        <Package className={`w-12 h-12 ${textMutedClass}`} />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className={`font-medium ${textClass} text-sm line-clamp-2 mb-1`}>{product.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className={`font-bold ${textClass}`}>{product.price} ₼</span>
                      {canAdd ? (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addToCart(product);
                          }}
                          className={`p-2 ${buttonClass} rounded-full transition-colors`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-xs text-red-500 font-medium px-2 py-1 bg-red-50 rounded-full">
                          Stokda bitdi
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              )})}
            </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {products.length === 0 && (
          <div className="px-6 pb-24 text-center">
            <p className={`${textMutedClass} mb-4`}>Tezliklə məhsullar əlavə olunacaq</p>
          </div>
        )}

        {/* Footer */}
        <div className={`py-8 px-4 ${textMutedClass} border-t ${borderClass}`}>
          {/* Social Media Links */}
          {customization && (customization.instagram || customization.facebook || customization.tiktok || customization.telegram || customization.linkedin) && (
            <div className="flex justify-center gap-4 mb-6">
              {customization.instagram && (
                <a href={customization.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              )}
              {customization.facebook && (
                <a href={customization.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              {customization.tiktok && (
                <a href={customization.tiktok} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              )}
              {customization.telegram && (
                <a href={customization.telegram} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                  </svg>
                </a>
              )}
              {customization.linkedin && (
                <a href={customization.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              )}
            </div>
          )}

          {/* Store Copyright */}
          <div className="text-center text-sm mb-4">
            © {new Date().getFullYear()} {store.store_name}
          </div>
        </div>
      </main>

      {/* Start Button - Fixed Bottom - Only show when cart has items */}
      {cartItemCount > 0 && (
        <div className={`fixed bottom-0 left-0 right-0 p-4 ${bgClass} border-t ${borderClass} z-30`}>
          <button
            onClick={handleCheckout}
            className={`w-full py-4 ${buttonClass} rounded-2xl font-medium text-lg transition-colors flex items-center justify-center gap-2`}
          >
            <ShoppingCart className="w-5 h-5" />
            Səbətə bax ({cartItemCount})
          </button>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          currentTheme={{ card: 'bg-white', button: 'bg-black', accent: 'text-black' }}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={addToCart}
          getCategoryName={getCategoryName}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          cart={cart}
          currentTheme={{ card: 'bg-white', button: 'bg-black', accent: 'text-black' }}
          customerData={customerData}
          setCustomerData={setCustomerData}
          submitting={submitting}
          onClose={() => setShowCheckout(false)}
          onSubmit={handleSubmitOrder}
          updateQuantity={updateQuantity}
          getTotal={getTotal}
        />
      )}

      {/* Sidebar Menu - Soldan sağa açılır */}
      <div 
        className={`fixed inset-0 z-50 transition-all duration-300 ${mobileMenuOpen ? 'visible' : 'invisible'}`}
      >
        {/* Overlay */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-50' : 'opacity-0'}`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Sidebar Panel */}
        <div 
          className={`absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {store.logo_url && (
                <img src={store.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              )}
              <span className="font-bold text-lg text-gray-900">{store.store_name}</span>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="p-5 space-y-6 overflow-y-auto h-[calc(100%-80px)]">
            {/* Ana səhifə */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-full text-left px-5 py-3.5 rounded-xl bg-gray-100 text-gray-800 flex items-center gap-3 text-base font-medium"
            >
              <StoreIcon className="w-5 h-5" />
              Ana səhifə
            </button>

            {/* Kateqoriyalar */}
            {categories.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Kateqoriyalar</h3>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-5 py-3.5 rounded-xl transition-all text-base font-medium ${
                        selectedCategory === cat.id ? 'bg-black text-white' : 'hover:bg-gray-100 text-gray-800'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Haqqımızda */}
            {(store.description || customization?.about) && (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowAbout(true);
                }}
                className="w-full text-left px-5 py-3.5 rounded-xl hover:bg-gray-100 text-gray-800 flex items-center gap-3 text-base font-medium"
              >
                <Info className="w-5 h-5" />
                Haqqımızda
              </button>
            )}

            {/* Ünvan */}
            {(store.address || store.city || store.google_maps_link) && (
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Ünvan</h3>
                <div className="bg-gray-50 rounded-2xl p-5">
                  {store.address && (
                    <p className="text-gray-800 text-base mb-1">{store.address}</p>
                  )}
                  {store.city && (
                    <p className="text-gray-500 text-sm mb-4">{store.city}</p>
                  )}
                  {store.google_maps_link && (
                    <a
                      href={store.google_maps_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-xl text-base font-medium"
                    >
                      <MapPin className="w-5 h-5" />
                      Xəritədə aç
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Əlaqə */}
            {storeWhatsApp && (
              <a
                href={`https://wa.me/${storeWhatsApp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-green-500 text-white px-5 py-4 rounded-xl text-base font-medium"
              >
                <MessageCircle className="w-6 h-6" />
                WhatsApp ilə əlaqə
              </a>
            )}

            {/* Dil */}
            <div className="flex items-center gap-3 px-5 py-3.5 text-gray-600">
              <span className="text-base">🌐</span>
              <span className="text-base">Azərbaycan</span>
            </div>
          </div>
        </div>
      </div>

      {/* About Modal - Haqqımızda */}
      {showAbout && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" 
          onClick={() => setShowAbout(false)}
        >
          <div 
            className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Info className="w-5 h-5" />
                Haqqımızda
              </h2>
              <button 
                onClick={() => setShowAbout(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-5 overflow-y-auto">
              {/* Store Logo & Name */}
              <div className="flex items-center gap-4 mb-6">
                {store.logo_url ? (
                  <img src={store.logo_url} alt={store.store_name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                    <StoreIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg">{store.store_name}</h3>
                  {store.city && <p className="text-gray-500 text-sm">{store.city}</p>}
                </div>
              </div>

              {/* Description */}
              {(store.description || customization?.about) && (
                <div className="mb-6">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {customization?.about || store.description}
                  </p>
                </div>
              )}

              {/* Address */}
              {(store.address || store.city) && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <h4 className="font-medium text-sm text-gray-500 mb-2">Ünvan</h4>
                  {store.address && <p className="text-gray-800">{store.address}</p>}
                  {store.city && <p className="text-gray-600 text-sm">{store.city}</p>}
                </div>
              )}

              {/* Contact Button */}
              {storeWhatsApp && (
                <a
                  href={`https://wa.me/${storeWhatsApp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 text-white px-5 py-3 rounded-xl font-medium w-full"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp ilə əlaqə
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Product Card Component (Grid Layout)
function ProductCard({ product, currentTheme, onAddToCart, onViewDetails }) {
  const mainImage = product.media?.[0]?.url || product.media?.[0] || product.image_url;
  const mediaCount = product.media?.length || (product.image_url ? 1 : 0);
  const hasVideo = product.media?.some(m => m.type === 'video');
  
  return (
    <div
      className={`${currentTheme.card} rounded-xl overflow-hidden shadow-sm hover:shadow-md active:scale-[0.98] transition-all cursor-pointer`}
      onClick={() => onViewDetails(product)}
    >
      <div className="relative aspect-square">
        {mainImage ? (
          <img
            src={mainImage}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <Package className="w-10 h-10 md:w-14 md:h-14 text-gray-200" />
          </div>
        )}
        {mediaCount > 1 && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs md:text-sm px-2 py-1 rounded-full flex items-center gap-1">
            {hasVideo ? <Video className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            {mediaCount}
          </div>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-medium text-sm md:text-base line-clamp-2 mb-2 leading-tight">{product.name}</h3>
        <div className="flex items-center justify-between gap-2">
          <span className={`font-bold text-base md:text-lg ${currentTheme.accent}`}>
            {product.price} <span className="text-xs md:text-sm font-normal">AZN</span>
          </span>
          <button 
            className={`${currentTheme.button} text-white p-2.5 md:p-3 rounded-xl hover:opacity-90 transition-opacity`}
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
          >
            <Plus className="w-5 h-5 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Product List Item Component (List Layout)
function ProductListItem({ product, currentTheme, onAddToCart, onViewDetails }) {
  const mainImage = product.media?.[0]?.url || product.media?.[0] || product.image_url;
  const mediaCount = product.media?.length || (product.image_url ? 1 : 0);
  
  return (
    <div
      className={`${currentTheme.card} rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer`}
      onClick={() => onViewDetails(product)}
    >
      <div className="flex gap-4 p-4 md:p-5">
        <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0">
          {mainImage ? (
            <img
              src={mainImage}
              alt={product.name}
              className="w-full h-full object-cover rounded-xl"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 rounded-xl flex items-center justify-center">
              <Package className="w-10 h-10 text-gray-200" />
            </div>
          )}
          {mediaCount > 1 && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              +{mediaCount}
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-base md:text-lg line-clamp-2 mb-2">{product.name}</h3>
          {product.description && (
            <p className="text-sm md:text-base text-gray-500 line-clamp-2 mb-3">{product.description}</p>
          )}
          <div className="flex items-center justify-between mt-auto">
            <span className={`font-bold text-lg md:text-xl ${currentTheme.accent}`}>
              {product.price} <span className="text-sm font-normal">AZN</span>
            </span>
            <button 
              className={`${currentTheme.button} text-white px-5 py-2.5 rounded-xl flex items-center gap-2 text-base hover:opacity-90 transition-opacity`}
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(product);
              }}
            >
              <Plus className="w-5 h-5" />
              <span className="hidden md:inline">Səbətə</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Product Detail Modal Component
function ProductDetailModal({ product, currentTheme, onClose, onAddToCart, getCategoryName }) {
  return (
    <div 
      className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" 
      onClick={onClose}
    >
      <div 
        className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Media */}
        <div className="relative aspect-square flex-shrink-0 bg-gray-100">
          <MediaGallery media={product.media?.length > 0 ? product.media : (product.image_url ? [{ url: product.image_url, type: 'image' }] : [])} />
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full p-2 shadow-lg hover:bg-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto">
          {product.category_id && (
            <span className="text-[10px] md:text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded mb-2 inline-block">
              {getCategoryName(product.category_id)}
            </span>
          )}
          <h2 className="text-lg md:text-xl font-bold mb-2">{product.name}</h2>
          {product.description && (
            <p className="text-gray-600 text-sm mb-4">{product.description}</p>
          )}
          <div className="flex items-center justify-between gap-3 pt-2 border-t">
            <span className={`text-2xl md:text-3xl font-bold ${currentTheme.accent}`}>
              {product.price} <span className="text-sm font-normal">AZN</span>
            </span>
            <Button 
              className={`${currentTheme.button} text-white`}
              onClick={() => {
                onAddToCart(product);
                onClose();
              }}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Səbətə
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Checkout Modal Component
function CheckoutModal({ cart, currentTheme, customerData, setCustomerData, submitting, onClose, onSubmit, updateQuantity, getTotal }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
        <div className="p-3 md:p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Səbət</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-3 md:p-4 overflow-y-auto flex-1">
          {cart.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">Səbət boşdur</p>
          ) : (
            <>
              {cart.map((item) => {
                const maxStock = item.stock || 999;
                const canIncrease = item.quantity < maxStock;
                return (
                <div key={item.id} className="flex items-center gap-2 py-2.5 border-b last:border-0">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {(item.media?.[0]?.url || item.image_url) && (
                      <img src={item.media?.[0]?.url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm line-clamp-1">{item.name}</h4>
                    <p className={`${currentTheme.accent} font-bold text-sm`}>{item.price} AZN</p>
                    {!canIncrease && (
                      <p className="text-xs text-red-500">Maks: {maxStock} ədəd</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button 
                      onClick={() => updateQuantity(item.id, -1)} 
                      className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                    <button 
                      onClick={() => canIncrease && updateQuantity(item.id, 1)} 
                      disabled={!canIncrease}
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                        canIncrease 
                          ? 'bg-gray-100 active:bg-gray-200' 
                          : 'bg-gray-50 cursor-not-allowed opacity-40'
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )})}
              
              <div className="py-3 border-t mt-3">
                <div className="flex justify-between text-base font-bold">
                  <span>Cəmi:</span>
                  <span className={currentTheme.accent}>{getTotal()} AZN</span>
                </div>
              </div>

              <form onSubmit={onSubmit} className="space-y-3 pt-3">
                <div>
                  <Label htmlFor="name" className="text-xs">Adınız *</Label>
                  <Input
                    id="name"
                    value={customerData.name}
                    onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                    required
                    className="h-10 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs">Telefon *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={customerData.phone}
                    onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                    required
                    placeholder="+994..."
                    className="h-10 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-xs">Qeyd</Label>
                  <Textarea
                    id="notes"
                    value={customerData.notes}
                    onChange={(e) => setCustomerData({...customerData, notes: e.target.value})}
                    rows={2}
                    className="text-sm mt-1"
                  />
                </div>
                <Button type="submit" className={`w-full ${currentTheme.button} text-white h-11`} disabled={submitting}>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {submitting ? 'Göndərilir...' : 'WhatsApp ilə Sifariş'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Media Gallery Component
function MediaGallery({ media }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (!media || media.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <Package className="w-16 h-16 text-gray-200" />
      </div>
    );
  }

  const currentMedia = media[currentIndex];
  const isVideo = currentMedia?.type === 'video' || currentMedia?.url?.includes('.mp4');

  return (
    <div className="relative w-full h-full">
      {isVideo ? (
        <video
          src={currentMedia.url || currentMedia}
          className="w-full h-full object-cover"
          controls
          playsInline
        />
      ) : (
        <img
          src={currentMedia?.url || currentMedia}
          alt="Product"
          className="w-full h-full object-cover"
        />
      )}
      
      {media.length > 1 && (
        <>
          <button 
            onClick={() => setCurrentIndex((prev) => (prev - 1 + media.length) % media.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur p-1.5 md:p-2 rounded-full shadow active:scale-95 transition-transform"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button 
            onClick={() => setCurrentIndex((prev) => (prev + 1) % media.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur p-1.5 md:p-2 rounded-full shadow active:scale-95 transition-transform"
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          
          {/* Thumbnails */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 max-w-[80%] overflow-x-auto scrollbar-hide bg-black/20 backdrop-blur rounded-full p-1.5">
            {media.slice(0, 5).map((m, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 flex-shrink-0 transition-all ${
                  index === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-60'
                }`}
              >
                {m.type === 'video' ? (
                  <div className="w-full h-full bg-black flex items-center justify-center">
                    <Play className="w-3 h-3 md:w-4 md:h-4 text-white" />
                  </div>
                ) : (
                  <img src={m.url || m} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
            {media.length > 5 && (
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-black/60 flex items-center justify-center text-white text-xs font-medium">
                +{media.length - 5}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Restaurant Menu Component
function RestaurantMenu({ menuItems, currentTheme, onAddToCart }) {
  const MENU_CATEGORIES = [
    { value: 'appetizers', label: 'Başlanğıclar', icon: '🥗' },
    { value: 'soups', label: 'Şorbalar', icon: '🍲' },
    { value: 'salads', label: 'Salatlar', icon: '🥙' },
    { value: 'main_course', label: 'Əsas Yeməklər', icon: '🍽️' },
    { value: 'desserts', label: 'Şirniyyatlar', icon: '🍰' },
    { value: 'drinks', label: 'İçkilər', icon: '🥤' },
  ];

  const groupedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  if (menuItems.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 md:p-12 text-center">
        <UtensilsCrossed className="w-12 h-12 md:w-16 md:h-16 text-gray-200 mx-auto mb-3" />
        <p className="text-gray-400 text-sm md:text-base">Menyu yoxdur</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {MENU_CATEGORIES.map(category => {
        const items = groupedItems[category.value];
        if (!items || items.length === 0) return null;

        return (
          <div key={category.value} className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="text-lg md:text-xl font-bold mb-4 flex items-center gap-2">
              <span>{category.icon}</span>
              <span>{category.label}</span>
            </h3>
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-3 p-3 border border-gray-100 rounded-lg hover:shadow-md transition-shadow"
                >
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-semibold text-sm md:text-base">{item.name}</h4>
                      <span className={`font-bold text-base md:text-lg ${currentTheme.accent} whitespace-nowrap`}>
                        {item.price} <span className="text-xs font-normal">AZN</span>
                      </span>
                    </div>
                    {item.description && (
                      <p className="text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                    )}
                    <Button
                      size="sm"
                      className={`${currentTheme.button} text-white mt-2`}
                      onClick={() => onAddToCart({ ...item, id: item.id, name: item.name, price: item.price })}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Səbətə əlavə et
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}